import React, { useImperativeHandle, useState } from 'react';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { safeDivide } from 'src/utils/app';

export interface SlPaginatorApi {
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export interface SlPaginatorProps {
  /**
   * Full list of items to paginate
   */
  items: unknown[];
  /**
   * Number of items that will be rendered on each page
   */
  itemsPerPage: number;
  /**
   * Whether the paginator is in a loading state
   */
  loading?: boolean;
  /**
   * Renders the loading state when `loading` is true
   */
  renderLoader?: () => React.ReactNode;
  /**
   * Renders the items for the current page. The slice
   * of items to render is passed as an argument.
   */
  renderItems: (items: unknown[]) => React.ReactNode;

  onPageChange?: (newPage: number) => void;
}

/**
 * Paginator component for paginating lists of items.
 * Should not be used for infinite pagination where the total
 * number of items is unknown.
 */
// eslint-disable-next-line react/display-name
export const SlPaginator = React.forwardRef(
  (
    { items, renderItems, itemsPerPage, loading, renderLoader, onPageChange }: SlPaginatorProps,
    ref: React.ForwardedRef<SlPaginatorApi>
  ) => {
    const [page, setPage] = useState(1);
    const totalPages = Math.ceil(safeDivide(items.length, itemsPerPage, 1));

    useImperativeHandle(ref, () => ({
      setPage
    }));

    const handleNextPage = () => {
      const newPage = Math.min(totalPages, page + 1);
      setPage(newPage);
      // eslint-disable-next-line no-unused-expressions
      onPageChange?.(newPage);
    };

    const handlePrevPage = () => {
      const newPage = Math.max(1, page - 1);
      setPage(newPage);
      // eslint-disable-next-line no-unused-expressions
      onPageChange?.(newPage);
    };

    if (loading) {
      return renderLoader?.();
    }

    return (
      <div>
        {renderItems(items.slice((page - 1) * itemsPerPage, page * itemsPerPage))}
        {totalPages > 1 && (
          <div
            style={{
              marginTop: '24px',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Pagination
              currentPage={page}
              onClickNext={handleNextPage}
              onClickPrevious={handlePrevPage}
              totalPages={totalPages}
            />
          </div>
        )}
      </div>
    );
  }
);
